import type { FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, Modal } from "../../../shared";

import styles from "./ConfirmDeleteTaskModal.module.css";

export type ConfirmDeleteTaskModalProps = {
  open: boolean;
  /** Name of the task being deleted — surfaced inline in the body copy. */
  taskName: string;
  onCancel: () => void;
  /** Fired only after the destructive CTA is clicked. The parent is
   *  responsible for actually removing the task and closing the modal. */
  onConfirm: () => void;
  /** When `true`, the destructive CTA renders disabled + in its loading
   *  state. Owned by the parent so it can block a double-click while the
   *  `DELETE /tasks/{task_id}` mutation is in flight — without it a second
   *  click fires a duplicate request that 404s once the first has removed
   *  the task. Defaults to `false`. */
  isDeleting?: boolean;
};

/**
 * Confirm-before-destroy modal shown when the admin clicks the `Delete`
 * button inside the Edit Task modal. Matches the destructive popup used for
 * deleting a challenge (red title, two outlined buttons — Go Back / Delete).
 */
const ConfirmDeleteTaskModal: FC<ConfirmDeleteTaskModalProps> = ({
  open,
  taskName,
  onCancel,
  onConfirm,
  isDeleting = false,
}) => (
  <Modal
    open={open}
    onClose={onCancel}
    intent="destructive"
    size="sm"
    // Unmount on close so the next open starts with a clean DOM (no stale
    // body, no leaked focus traps) and tests can assert by presence.
    destroyOnHidden
    title={
      <span className={styles.title}>
        {AppStrings.DELETE_TASK_MODAL_TITLE}
      </span>
    }
    footer={
      <div className={styles.footer}>
        <Button
          intent="neutral"
          variant="outline"
          onClick={onCancel}
          data-testid="task-confirm-delete-go-back"
        >
          {AppStrings.DELETE_TASK_GO_BACK}
        </Button>
        <Button
          intent="danger"
          variant="outline"
          // Outline variant defaults to white text (via
          // `--COLOR-LIGHT_TEXT_COLOR`); for the destructive popup the
          // Figma uses red text matching the border, so override inline.
          style={{ color: "var(--COLOR-LOST_COLOR)" }}
          onClick={onConfirm}
          // Block a double-click from firing a duplicate DELETE while the
          // first request is still settling.
          disabled={isDeleting}
          loading={isDeleting}
          data-testid="task-confirm-delete-confirm"
        >
          {AppStrings.DELETE_TASK_CONFIRM_CTA}
        </Button>
      </div>
    }
  >
    <div data-testid="task-confirm-delete-modal">
      <p className={styles.body}>
        {`${AppStrings.DELETE_TASK_CONFIRM_PREFIX} `}
        <span className={styles.bodyName}>{`"${taskName}"`}</span>
      </p>
    </div>
  </Modal>
);

export default ConfirmDeleteTaskModal;
